/**
 * User Detail Page Component
 *
 * Displays detailed information about a specific user with two tabs:
 * - Overview: User profile information, consents, interests
 * - Transactions: Transaction history with pagination
 *
 * Features:
 * - Fetches user data and transaction history
 * - Displays balance prominently
 * - Shows consents and interests
 * - Transaction table with ID, Amount, Type, and Date columns
 * - Loading indicators and empty states
 * - Smooth animations and Russian language interface
 *
 * Requirements:
 * - 5.1-5.9: User details view with transaction history
 */

import React, { useState } from "react";
import { useShow, useList } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import {
  Card,
  Descriptions,
  List,
  Statistic,
  Tabs,
  Table,
  Tag,
  Typography,
  Space,
  Spin,
  Empty,
  Button,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { UserResponse, TransactionResponse } from "../../api-client";
import { AddBonusModal } from "./components";
import { TasksTable } from "../campaigns/components/TasksTable";

const { Text, Title } = Typography;
const { TabPane } = Tabs;

/**
 * User detail page component.
 *
 * Displays user information with Overview and Transactions tabs.
 */
export const UserShow: React.FC = () => {
  const { query } = useShow<UserResponse>();
  const isLoading = query.isLoading;
  const user = query.data?.data;
  const userId = user?.telegram_id;

  // Modal state for bonus addition
  const [isBonusModalVisible, setIsBonusModalVisible] = useState(false);

  // Fetch transaction history for the user
  const transactionsQueryResult = useList<TransactionResponse>({
    resource: "transactions",
    pagination: {
      pageSize: 20,
    },
    meta: {
      user_id: userId,
    },
    queryOptions: {
      enabled: !!userId, // Only fetch when user ID is available
    },
  });
  const transactionsData = transactionsQueryResult.result?.data || [];
  const transactionsLoading = transactionsQueryResult.query.isLoading || false;
  const refetchTransactions = transactionsQueryResult.query.refetch;

  /**
   * Handle successful bonus transaction
   * Refetches user data and transaction history
   */
  const handleBonusSuccess = () => {
    query?.refetch(); // Refresh user balance
    refetchTransactions?.(); // Refresh transaction list
  };

  /**
   * Table column definitions for transaction history.
   */
  const transactionColumns: ColumnsType<TransactionResponse> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 200,
      render: (value: string) => (
        <Text code copyable={{ text: value }}>
          {value.substring(0, 8)}...
        </Text>
      ),
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      render: (value: number) => {
        const isPositive = value > 0;
        return (
          <Space>
            {isPositive ? (
              <ArrowUpOutlined style={{ color: "#52c41a" }} />
            ) : (
              <ArrowDownOutlined style={{ color: "#ff4d4f" }} />
            )}
            <Text
              strong
              style={{ color: isPositive ? "#52c41a" : "#ff4d4f" }}
            >
              {isPositive ? "+" : ""}
              {value}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Тип",
      dataIndex: "transaction_type",
      key: "transaction_type",
      width: 150,
      render: (value: string) => {
        const typeConfig: Record<
          string,
          { color: string; label: string }
        > = {
          bonus: { color: "success", label: "Начисление" },
          redemption: { color: "warning", label: "Списание" },
          visit: { color: "processing", label: "Визит" },
          manual: { color: "default", label: "Ручное" },
        };

        const config = typeConfig[value] || {
          color: "default",
          label: value,
        };

        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Дата",
      key: "date",
      width: 180,
      render: (_: unknown, record: TransactionResponse) => {
        const effectiveIso = record.transaction_at ?? record.created_at;
        const effective = dayjs(effectiveIso);
        const isBackdated =
          !!record.transaction_at &&
          record.transaction_at !== record.created_at;
        const text = (
          <Text>{effective.format("DD.MM.YYYY HH:mm")}</Text>
        );
        if (!isBackdated) return text;
        const created = dayjs(record.created_at);
        return (
          <Tooltip
            title={`Запись создана: ${created.format("DD.MM.YYYY HH:mm")}`}
          >
            <Space size={4}>
              {text}
              <Text type="secondary" style={{ fontSize: 12 }}>
                (задним числом)
              </Text>
            </Space>
          </Tooltip>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Empty description="Пользователь не найден" />
      </div>
    );
  }

  type CampaignHistoryEntry = {
    status?: string;
    participated_at?: string | null;
    completed_at?: string | null;
  };

  const campaignHistoryEntries: [string, CampaignHistoryEntry][] =
    user.campaign_history
      ? (Object.entries(user.campaign_history) as [
          string,
          CampaignHistoryEntry,
        ][])
      : [];

  const statusTagColors: Record<string, string> = {
    pending: "default",
    processing: "processing",
    completed: "success",
    done: "success",
    failed: "error",
  };

  const formatDateTime = (value?: string | null) =>
    value ? dayjs(value).format("DD.MM.YYYY HH:mm:ss") : "—";

  return (
    <Show
      title={`Пользователь: ${user.username ? `@${user.username}` : user.telegram_id}`}
      breadcrumb={
        <div>
          Пользователи / Информация
        </div>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* User Header with Balance */}
        <Card>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Space
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <div>
                <Title level={3} style={{ marginBottom: 0 }}>
                  {user.username ? `@${user.username}` : "Без username"}
                </Title>
                <Text type="secondary">Telegram ID: {user.telegram_id}</Text>
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsBonusModalVisible(true)}
                size="large"
              >
                Изменить баланс
              </Button>
            </Space>
            <Statistic
              title="Баланс"
              value={user.balance}
              valueStyle={{
                color: user.balance > 0 ? "#52c41a" : "#8c8c8c",
                fontSize: "32px",
                fontWeight: "bold",
              }}
            />
          </Space>
        </Card>

        {/* Tabs: Overview, Transactions, and Campaign Tasks */}
        <Card>
          <Tabs defaultActiveKey="overview">
            {/* Overview Tab */}
            <TabPane tab="Обзор" key="overview">
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <Descriptions column={1} bordered>
                  <Descriptions.Item label="Согласие на обработку данных">
                    {user.consent_privacy ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        Да
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="default">
                        Нет
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Согласие на персональные данные">
                    {user.consent_personal_data ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        Да
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="default">
                        Нет
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Согласие на маркетинг">
                    {user.consent_marketing ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        Да
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="default">
                        Нет
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Интересы">
                    {user.interests && user.interests.length > 0 ? (
                      <Space wrap>
                        {user.interests.map((interest: string) => (
                          <Tag key={interest} color="blue">
                            {interest}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary">Нет интересов</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Дата регистрации">
                    {dayjs(user.created_at).format("DD.MM.YYYY HH:mm:ss")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Последнее обновление">
                    {dayjs(user.updated_at).format("DD.MM.YYYY HH:mm:ss")}
                  </Descriptions.Item>
                </Descriptions>

                <Card title="История кампаний">
                  {campaignHistoryEntries.length > 0 ? (
                    <List
                      dataSource={campaignHistoryEntries}
                      renderItem={([campaignId, history]) => {
                        const status = history.status ?? "unknown";
                        const tagColor = statusTagColors[status] ?? "default";

                        return (
                          <List.Item>
                            <Space
                              direction="vertical"
                              size={4}
                              style={{ width: "100%" }}
                            >
                              <Space size={8} wrap>
                                <Typography.Link href={`/campaigns/show/${campaignId}`}>
                                  {campaignId}
                                </Typography.Link>
                                <Tag color={tagColor}>{status}</Tag>
                              </Space>
                              <Space size={16} wrap>
                                <Text type="secondary">
                                  Участие: {formatDateTime(history.participated_at)}
                                </Text>
                                <Text type="secondary">
                                  Завершено: {formatDateTime(history.completed_at)}
                                </Text>
                              </Space>
                            </Space>
                          </List.Item>
                        );
                      }}
                    />
                  ) : (
                    <Empty description="Пользователь еще не участвовал в кампаниях" />
                  )}
                </Card>
              </Space>
            </TabPane>

            {/* Transactions Tab */}
            <TabPane tab="Транзакции" key="transactions">
              {transactionsLoading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spin tip="Загрузка транзакций..." />
                </div>
              ) : (
                <Table
                  dataSource={transactionsData || []}
                  columns={transactionColumns}
                  rowKey="id"
                  loading={transactionsLoading}
                  pagination={{
                    pageSize: 20,
                    total: transactionsQueryResult.result?.total,
                    showSizeChanger: false,
                    showTotal: (total) => `Всего ${total} транзакций`,
                  }}
                  locale={{
                    emptyText: "Нет транзакций",
                  }}
                  scroll={{ x: 900 }}
                />
              )}
            </TabPane>

            {/* Campaign Tasks Tabs */}
            <TabPane tab="Активные задачи кампаний" key="active-campaign-tasks">
              <TasksTable
                userId={userId}
                archived={false}
                showCampaignLink
              />
            </TabPane>
            <TabPane tab="Архив задач кампаний" key="archived-campaign-tasks">
              <TasksTable userId={userId} archived showCampaignLink />
            </TabPane>
          </Tabs>
        </Card>
      </Space>

      {/* Add Bonus Modal */}
      {user && (
        <AddBonusModal
          userId={user.telegram_id}
          username={user.username}
          visible={isBonusModalVisible}
          onClose={() => setIsBonusModalVisible(false)}
          onSuccess={handleBonusSuccess}
        />
      )}
    </Show>
  );
};

export default UserShow;
